// MACHINERY V2 (Stroje) — MIDAS / LIVE / OEM dot cluster (M2.9).
//
// Reference impl: design_handoff_machinery_v3/machinery-readiness.jsx
// (`RdDot`, `ReadinessDots`).
//
// Its own module for the same reason M2.1 extracted StatusIcon.tsx: TWO
// separate M2.9 surfaces consume it (the popup header pill and the profile
// card), so it is a shared atom, not a premature abstraction — and folding it
// back into CapabilityBadge.tsx pushes that file over the 300-line rule.
//
// ABSENCE = UNLIT, NEVER HIDDEN: all three dots always render, in this order.
// The LIVE dot is drawn from `liveVisual` (lit / hollow / unlit), which
// midasReadiness.ts declares the RENDERING source of truth for every readiness
// surface in the app — never map lit/hollow inline anywhere else.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2ReadinessPresentation, RD } from "./readinessPresentation";

const Dot: React.FC<{
  on: boolean;
  hollow?: boolean;
  color: string;
  title: string;
}> = ({ on, hollow = false, color, title }) => (
  <span
    title={title}
    style={{
      width: 8,
      height: 8,
      borderRadius: 999,
      flexShrink: 0,
      background: on ? color : hollow ? "transparent" : RD.off,
      border: hollow ? "1.5px solid " + RD.hollow : "none",
    }}
  />
);

export interface ReadinessDotsProps {
  presentation: MachineryV2ReadinessPresentation;
  /** Wrap in the popup-header pill (bg + border + radius). */
  pill?: boolean;
}

const ReadinessDots: React.FC<ReadinessDotsProps> = ({ presentation: p, pill = false }) => {
  const { translate } = useTranslation("myMachinery");

  const cluster = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Dot on={p.midasLit} color={RD.midas} title={translate("myMachinery:midasDocs")} />
        <Dot
          on={p.liveVisual === "lit"}
          hollow={p.liveVisual === "hollow"}
          color={RD.live}
          title={translate("myMachinery:liveData")}
        />
        <Dot on={p.oemLit} color={RD.oem} title={translate("myMachinery:oemPackage")} />
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
          color: p.midasLit ? "var(--muted)" : RD.offText,
        }}
      >
        {/* Doc phrase from the util's own summary parts, so this label can
            never disagree with the card row or the tooltip. */}
        {p.summary.docPart || translate("myMachinery:midasNoDocsYet")}
      </span>
    </span>
  );

  if (!pill) return cluster;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 26,
        padding: "0 10px",
        borderRadius: 999,
        background: "var(--bg-soft)",
        border: "1px solid var(--border)",
        flexShrink: 0,
      }}
    >
      {cluster}
    </span>
  );
};

export default ReadinessDots;
